// /opt/isp_erp/frontend/src/pages/admin/finanzas/FacturasList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { facturasApi } from '../../../api/finanzas/facturas.api';
import { format } from 'date-fns';
import './FacturasList.css';

export default function FacturasList() {
  const navigate = useNavigate();
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarEliminadas, setMostrarEliminadas] = useState(false);
  const [filtros, setFiltros] = useState({
    estado: '',
    fecha_desde: '',
    fecha_hasta: ''
  });

  const estados = [
    { value: 'PENDIENTE', label: 'Pendientes', color: 'yellow' },
    { value: 'PAGADA', label: 'Pagadas', color: 'green' },
    { value: 'PARCIAL', label: 'Parciales', color: 'orange' },
    { value: 'ANULADA', label: 'Anuladas', color: 'red' },
    { value: 'VENCIDA', label: 'Vencidas', color: 'gray' }
  ];

  useEffect(() => {
    cargarFacturas();
  }, [filtros, mostrarEliminadas]);

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 100,
        incluir_eliminadas: mostrarEliminadas
      };

      if (filtros.estado && filtros.estado.trim() !== '') {
        params.estado = filtros.estado;
      }

      if (filtros.fecha_desde && filtros.fecha_desde.trim() !== '') {
        params.fecha_desde = filtros.fecha_desde;
      }

      if (filtros.fecha_hasta && filtros.fecha_hasta.trim() !== '') {
        params.fecha_hasta = filtros.fecha_hasta;
      }

      const response = await facturasApi.listar(params);
      setFacturas(response.data);
    } catch (error) {
      console.error('Error cargando facturas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestaurar = async (id) => {
    if (!window.confirm('¿Restaurar esta factura?')) return;

    try {
      await facturasApi.restaurar(id);
      cargarFacturas();
    } catch (error) {
      alert('Error al restaurar');
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      return format(new Date(fecha), 'dd/MM/yyyy');
    } catch {
      return 'Fecha inválida';
    }
  };

  const formatearFechaHora = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      return format(new Date(fecha), 'dd/MM/yyyy HH:mm');
    } catch {
      return 'Fecha inválida';
    }
  };

  // ✅ Exportar a Excel
  const handleExportarExcel = async () => {
    try {
      const params = {};
      if (filtros.estado && filtros.estado.trim() !== '') {
        params.estado = filtros.estado;
      }
      if (filtros.fecha_desde && filtros.fecha_desde.trim() !== '') {
        params.fecha_desde = filtros.fecha_desde;
      }
      if (filtros.fecha_hasta && filtros.fecha_hasta.trim() !== '') {
        params.fecha_hasta = filtros.fecha_hasta;
      }

      const response = await facturasApi.exportarExcel(params);

      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturas_${new Date().toISOString().slice(0, 19)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      alert('✅ Excel exportado correctamente');
    } catch (error) {
      console.error('Error exportando:', error);
      alert('Error al exportar');
    }
  };

  // ✅ Enviar factura por email
  const handleEnviarEmailFactura = async (facturaId, emailCliente) => {
    let email = emailCliente;
    if (!email) {
      email = prompt('Ingrese el email de destino:');
      if (!email) return;
    }

    try {
      await facturasApi.enviarPorEmail(facturaId, { email });
      alert('✅ Factura enviada por email correctamente');
    } catch (error) {
      console.error('Error enviando:', error);
      alert('Error al enviar el email: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="facturas-loading-container">
        <div className="facturas-loading-spinner"></div>
        <p>Cargando facturas...</p>
      </div>
    );
  }

  return (
    <div className="facturas-container">
      {/* Header */}
      <div className="facturas-header">
        <div className="facturas-header-left">
          <div className="facturas-header-icon">📄</div>
          <div>
            <h1>Facturas</h1>
            <p>Gestión de facturación</p>
          </div>
        </div>
        <div className="facturas-header-actions">
          {/* ✅ Botón Exportar Excel */}
          <button
            onClick={handleExportarExcel}
            className="facturas-btn-excel"
            title="Exportar a Excel"
          >
            📊 Exportar Excel
          </button>
          <button
            onClick={() => setMostrarEliminadas(!mostrarEliminadas)}
            className={`facturas-btn-toggle ${mostrarEliminadas ? 'active' : ''}`}
            title={mostrarEliminadas ? 'Ver facturas activas' : 'Ver facturas eliminadas'}
          >
            {mostrarEliminadas ? '📂 Activas' : '🗑️ Eliminadas'}
          </button>
          <button
            onClick={() => navigate('/finanzas/facturas/nueva')}
            className="facturas-btn-primary"
          >
            ➕ Nueva Factura
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="facturas-filtros">
        <div className="filtros-grid">
          <select
            value={filtros.estado}
            onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
            className="filtro-select"
          >
            <option value="">Todos los estados</option>
            {estados.map(e => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>

          <div className="filtro-fecha">
            <label>Desde:</label>
            <input
              type="date"
              value={filtros.fecha_desde}
              onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})}
              className="filtro-input"
            />
          </div>

          <div className="filtro-fecha">
            <label>Hasta:</label>
            <input
              type="date"
              value={filtros.fecha_hasta}
              onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})}
              className="filtro-input"
            />
          </div>

          <button
            onClick={() => setFiltros({ estado: '', fecha_desde: '', fecha_hasta: '' })}
            className="filtro-btn-limpiar"
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="facturas-table-container">
        <table className="facturas-table">
          <thead>
            <tr>
              <th>Número</th>
              <th>Cliente</th>
              <th>Email</th>
              <th>Fecha Emisión</th>
              <th>Vencimiento</th>
              <th className="text-right">Total</th>
              <th>Estado</th>
              {mostrarEliminadas && (
                <>
                  <th>Eliminado por</th>
                  <th>Motivo</th>
                  <th>Fecha Eliminación</th>
                </>
              )}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {facturas.length === 0 ? (
              <tr>
                <td colSpan={mostrarEliminadas ? 11 : 8} className="facturas-empty">
                  <div className="empty-icon">📄</div>
                  <p className="empty-title">No hay facturas para mostrar</p>
                  <p className="empty-subtitle">
                    {mostrarEliminadas
                      ? 'No hay facturas eliminadas'
                      : 'Crea una nueva factura para comenzar'}
                  </p>
                 </td>
               </tr>
            ) : (
              facturas.map(f => (
                <tr key={f.id} className={`factura-row ${f.eliminado ? 'eliminada' : ''}`}>
                  <td className="factura-numero">{f.numero_factura} </td>
                  <td className="factura-cliente">{f.customer_nombre} </td>
                  <td className="factura-email">{f.customer_email || '-'} </td>
                  <td className="factura-fecha">{formatearFecha(f.fecha_emision)}</td>
                  <td className="factura-fecha">{formatearFecha(f.fecha_vencimiento)}</td>
                  <td className="factura-total text-right">${f.total?.toFixed(2)}</td>
                  <td>
                    <span className={`factura-estado estado-${f.estado?.toLowerCase()}`}>
                      {f.estado}
                    </span>
                  </td>
                  {mostrarEliminadas && (
                    <>
                      <td className="factura-eliminado-por">{f.eliminado_por || 'N/A'}</td>
                      <td className="factura-motivo" title={f.motivo_eliminacion}>
                        {f.motivo_eliminacion || 'N/A'}
                      </td>
                      <td className="factura-fecha-eliminacion">
                        {formatearFechaHora(f.fecha_eliminacion)}
                      </td>
                    </>
                  )}
                  <td className="factura-acciones">
                    {mostrarEliminadas ? (
                      <button
                        onClick={() => handleRestaurar(f.id)}
                        className="factura-btn-restaurar"
                        title="Restaurar factura"
                      >
                        Restaurar
                      </button>
                    ) : (
                      <div className="acciones-group">
                        <button
                          onClick={() => navigate(`/finanzas/facturas/${f.id}`)}
                          className="factura-btn-ver"
                          title="Ver detalle de factura"
                        >
                          Ver
                        </button>
                        {/* ✅ Botón de email */}
                        <button
                          onClick={() => handleEnviarEmailFactura(f.id, f.customer_email)}
                          className="factura-btn-email"
                          title="Enviar por email"
                        >
                          📧
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Estadísticas rápidas */}
      {facturas.length > 0 && !mostrarEliminadas && (
        <div className="facturas-stats">
          <div className="stats-card">
            <span className="stats-label">Total facturado</span>
            <span className="stats-value">
              ${facturas.reduce((sum, f) => sum + (f.total || 0), 0).toFixed(2)}
            </span>
          </div>
          <div className="stats-card">
            <span className="stats-label">Facturas pendientes</span>
            <span className="stats-value">
              {facturas.filter(f => f.estado === 'PENDIENTE').length}
            </span>
          </div>
          <div className="stats-card">
            <span className="stats-label">Facturas pagadas</span>
            <span className="stats-value">
              {facturas.filter(f => f.estado === 'PAGADA').length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
